import type { ReactNode } from 'react';
import { useLanguage } from '@hooks/useLanguage';

/**
 * Wraps an icon that represents "back" or "previous" (e.g. arrow-left).
 * In RTL, the icon is mirrored so it points in the logical "back" direction (right in RTL).
 */
export function RtlBackIcon({ children }: { children: ReactNode }) {
  const { isRtl } = useLanguage();
  return (
    <span className={isRtl ? 'inline-block scale-x-[-1]' : undefined} aria-hidden>
      {children}
    </span>
  );
}

/**
 * Wraps an icon that represents "forward" or "next" (e.g. arrow-right).
 * In RTL, the icon is mirrored so it points in the logical "forward" direction (left in RTL).
 */
export function RtlForwardIcon({ children }: { children: ReactNode }) {
  const { isRtl } = useLanguage();
  return (
    <span className={isRtl ? 'inline-block scale-x-[-1]' : undefined} aria-hidden>
      {children}
    </span>
  );
}

/**
 * Wraps a chevron or arrow that should flip in RTL (e.g. sidebar collapse arrow).
 * Use for directional chevrons that indicate "expand/collapse" or "go back/forward".
 */
export function RtlDirectionalIcon({ children }: { children: ReactNode }) {
  const { isRtl } = useLanguage();
  return (
    <span className={isRtl ? 'inline-block scale-x-[-1]' : undefined} aria-hidden>
      {children}
    </span>
  );
}
